import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Grid,
  Card,
  CardContent,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Switch,
} from '@mui/material';
import {
  Add,
  Delete,
  CardMembership,
  Star,
  StarBorder,
  Settings,
} from '@mui/icons-material';
import apiService from '../../services/api';

const SubscriptionPlansManagement = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [openFeaturesDialog, setOpenFeaturesDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [features, setFeatures] = useState([]);
  const [planFeatures, setPlanFeatures] = useState([]);
  const [statistics, setStatistics] = useState({});

  useEffect(() => {
    // SuperAdmin endpoints have no restrictions - fetch data directly
    fetchPlans();
    fetchFeatures();
    fetchStatistics();
  }, []);

  useEffect(() => {
    if (showAllPlans !== undefined) {
      fetchPlans();
    }
  }, [showAllPlans]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      
      // Use SuperAdmin endpoints (no permission checks)
      const endpoint = showAllPlans ? '/superadmin/subscriptions/plans-all' : '/superadmin/subscriptions/plans';
      console.log('Using SuperAdmin endpoint:', endpoint);
      
      const response = await apiService.get(endpoint);
      if (response.success) {
        setPlans(response.data.plans || []);
      } else {
        setError('Failed to fetch subscription plans');
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Error fetching plans: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeatures = async () => {
    try {
      const response = await apiService.get('/superadmin/subscriptions/features');
      if (response.success) {
        setFeatures(response.data.features || []);
      }
    } catch (err) {
      console.error('Error fetching features:', err);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await apiService.get('/superadmin/subscriptions/statistics');
      if (response.success) {
        setStatistics(response.data.statistics || []);
      }
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };

  const handleManageFeatures = async (plan) => {
    try {
      setSelectedPlan(plan);
      // Fetch current plan features
      const response = await apiService.get(`/superadmin/subscription-plan/${plan.id}/features`);
      if (response.success) {
        setPlanFeatures(response.data.features || []);
      }
      setOpenFeaturesDialog(true);
    } catch (err) {
      setError('Error fetching plan features: ' + err.message);
    }
  };

  const handleFeatureToggle = (featureId, isIncluded) => {
    const normalizedFeatureId = Number(featureId);
    setPlanFeatures(prev => 
      prev.map(pf => 
        Number(pf.feature_id ?? pf.id) === normalizedFeatureId
          ? { ...pf, feature_id: pf.feature_id ?? pf.id, is_included: isIncluded ? 1 : 0 }
          : pf
      )
    );
  };

  const handleSaveFeatures = async () => {
    try {
      const response = await apiService.put(`/superadmin/subscription-plan/${selectedPlan.id}/features`, {
        features: planFeatures
      });
      
      if (response.success) {
        setOpenFeaturesDialog(false);
        setSelectedPlan(null);
        setPlanFeatures([]);
        // Refresh plans to show updated feature counts
        fetchPlans();
      } else {
        setError(response.message || 'Failed to update plan features');
      }
    } catch (err) {
      setError('Error updating plan features: ' + err.message);
    }
  };

  const handleCreatePlan = () => {
    navigate('/superadmin/subscription-plan/new');
  };

  const handleEditPlan = (plan) => {
    navigate(`/superadmin/subscription-plan/${plan.id}`);
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiService.delete(`/superadmin/subscription-plan/${planId}`);
      if (response.success) {
        fetchPlans();
        fetchStatistics();
      } else {
        setError(response.message || 'Failed to delete plan');
      }
    } catch (err) {
      setError('Error deleting plan: ' + err.message);
    }
  };

  const handleTogglePopularity = async (planId) => {
    try {
      const response = await apiService.put(`/superadmin/subscription-plan/${planId}/popularity`);
      if (response.success) {
        fetchPlans();
      } else {
        setError(response.message || 'Failed to update plan popularity');
      }
    } catch (err) {
      setError('Error updating plan popularity: ' + err.message);
    }
  };

  const handleToggleActiveStatus = async (planId, currentStatus) => {
    try {
      const response = await apiService.put(`/superadmin/subscription-plan/${planId}`, {
        is_active: currentStatus ? 0 : 1
      });
      if (response.success) {
        fetchPlans();
      } else {
        setError(response.message || 'Failed to update plan status');
      }
    } catch (err) {
      setError('Error updating plan status: ' + err.message);
    }
  };

  const getDurationText = (type, value, bonusNumber, bonusType) => {
    let durationText = '';
    if (value === 1) {
      durationText = `1 ${type.slice(0, -1)}`;
    } else {
      durationText = `${value} ${type}`;
    }
    
    // Add bonus information if available
    if (bonusNumber && bonusNumber > 0 && bonusType) {
      const bonusText = bonusNumber === 1 ? `1 ${bonusType.slice(0, -1)}` : `${bonusNumber} ${bonusType}`;
      durationText += ` + ${bonusText} bonus`;
    }
    
    return durationText;
  };

  const getBillingText = (cycle) => {
    const cycleMap = {
      'monthly': 'Monthly',
      'quarterly': 'Quarterly',
      'yearly': 'Yearly',
      'one_time': 'One Time'
    };
    return cycleMap[cycle] || cycle;
  };

  const getCurrencySymbol = (currency) => {
    const currencyMap = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'INR': '₹'
    };
    return currencyMap[currency] || currency;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <CardMembership color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary.main">
              Subscription Plans
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage subscription plans, pricing, and features
            </Typography>
          </Box>
        </Box>
        
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={showAllPlans}
                onChange={(e) => setShowAllPlans(e.target.checked)}
              />
            }
            label="Show All Plans (Including Inactive)"
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            size="large"
            onClick={handleCreatePlan}
          >
            Create New Plan
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {statistics.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {statistics.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {stat.name}
                  </Typography>
                  <Typography variant="h4" component="div" color="primary.main" fontWeight="bold">
                    {getCurrencySymbol(stat.currency)}{stat.price}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.total_subscriptions} subscriptions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.active_subscriptions} active
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Plans Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Plan Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell>Billing</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Popular</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => (
                <TableRow key={plan.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CardMembership color="primary" />
                      <Typography variant="body2" fontWeight="medium">
                        {plan.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {plan.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {getDurationText(plan.duration_type, plan.duration_value, plan.bonus_number, plan.bonus_type)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box>
                      {plan.offer_price && parseFloat(plan.offer_price) > 0 ? (
                        <>
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            {getCurrencySymbol(plan.currency)}{plan.offer_price}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                            {getCurrencySymbol(plan.currency)}{plan.price}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" fontWeight="bold">
                          {getCurrencySymbol(plan.currency)}{plan.price}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getBillingText(plan.billing_cycle)} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell>
                    <Chip 
                      label={plan.is_active == 1 ? 'Active' : 'Inactive'} 
                      color={plan.is_active == 1 ? 'success' : 'default'} 
                      size="small"
                      onClick={() => handleToggleActiveStatus(plan.id, plan.is_active)}
                      sx={{ cursor: 'pointer' }}
                      title={plan.is_active ? 'Click to deactivate' : 'Click to activate'}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleTogglePopularity(plan.id)}
                      color={plan.is_popular ? 'warning' : 'default'}
                    >
                      {plan.is_popular ? <Star /> : <StarBorder />}
                    </IconButton>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={1} justifyContent="center">
                      <Tooltip title="Manage Features">
                        <IconButton
                          size="small"
                          onClick={() => handleManageFeatures(plan)}
                          color="primary"
                        >
                          <Settings />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Plan">
                        <IconButton
                          size="small"
                          onClick={() => handleEditPlan(plan)}
                          color="primary"
                        >
                          <Settings />
                        </IconButton>
                      </Tooltip>
                      {/* <Tooltip title="Delete Plan">
                        <IconButton
                          size="small"
                          onClick={() => handleDeletePlan(plan.id)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip> */}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {plans.length === 0 && (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" color="text.secondary">
              No subscription plans found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Create your first subscription plan to get started
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Features Management Dialog */}
      <Dialog open={openFeaturesDialog} onClose={() => setOpenFeaturesDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Manage Features for {selectedPlan?.name}
        </DialogTitle>
        <DialogContent>
          {selectedPlan && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Select which features are included in this plan and set their limits.
              </Typography>
              
              <FormGroup>
                {features.map((feature) => {
                  const featureId = Number(feature.id);
                  const planFeature = planFeatures.find(pf => Number(pf.feature_id ?? pf.id) === featureId);
                  const isIncluded = Number(planFeature?.is_included ?? planFeature?.is_enabled ?? 0) === 1;
                  
                  return (
                    <Box key={feature.id} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isIncluded}
                            onChange={(e) => handleFeatureToggle(feature.id, e.target.checked)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="subtitle1" fontWeight="medium">
                              {feature.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {feature.description}
                            </Typography>
                            <Chip 
                              label={feature.category} 
                              size="small" 
                              variant="outlined" 
                              sx={{ mt: 1 }}
                            />
                          </Box>
                        }
                      />
                    </Box>
                  );
                })}
              </FormGroup>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFeaturesDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveFeatures} 
            variant="contained"
          >
            Save Features
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionPlansManagement;
